import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';

function escapeAttrValue(value) {
  return typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
    ? CSS.escape(String(value))
    : String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

/** Finds the PDP variant-picker in fetched HTML (avoids quick-add / duplicate product-id pickers). */
function findVariantPickerInDocument(doc, instance) {
  const blockId = instance.dataset.blockId;
  if (blockId) {
    const byBlock = doc.querySelector(`variant-picker[data-block-id="${escapeAttrValue(blockId)}"]`);
    if (byBlock) return byBlock;
  }

  const productId = instance.dataset.productId;
  const safePid = productId ? escapeAttrValue(productId) : '';

  const liveSection = instance.closest('.shopify-section');
  const liveSectionId = liveSection?.id;
  if (liveSectionId && safePid && 'getElementById' in doc && typeof doc.getElementById === 'function') {
    const fetchedSection = doc.getElementById(liveSectionId);
    const inSameSection = fetchedSection?.querySelector(`variant-picker[data-product-id="${safePid}"]`);
    if (inSameSection) return inSameSection;
  }

  if (safePid) {
    const mainPdp =
      doc.querySelector(`variant-picker[data-product-id="${safePid}"][data-template-product-match="true"]`) ||
      doc.querySelector(`variant-picker[data-product-id="${safePid}"][ref="mainVariantPicker"]`);
    if (mainPdp) return mainPdp;

    const byProduct = doc.querySelector(`variant-picker[data-product-id="${safePid}"]`);
    if (byProduct) return byProduct;
  }

  return doc.querySelector('variant-picker');
}

/** Variant JSON for the selected variant when multiple pickers share the same product id. */
function resolveVariantJsonText(root, matchedPicker, expectedVariantId, documentRoot) {
  const tryParse = (text) => {
    if (!text?.trim()) return null;
    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  };

  const fromPicker = matchedPicker?.querySelector('script[type="application/json"]')?.textContent;
  const parsedFromPicker = tryParse(fromPicker);
  if (
    parsedFromPicker &&
    (!expectedVariantId || String(parsedFromPicker.id) === String(expectedVariantId))
  ) {
    return fromPicker;
  }

  const searchRoots = [];
  if (root) searchRoots.push(root);
  if (documentRoot && documentRoot !== root) searchRoots.push(documentRoot);

  if (expectedVariantId) {
    for (const r of searchRoots) {
      for (const script of r.querySelectorAll('variant-picker script[type="application/json"]')) {
        const text = script.textContent;
        const v = tryParse(text);
        if (v && String(v.id) === String(expectedVariantId)) return text;
      }
    }
  }

  if (fromPicker) return fromPicker;

  return (
    root?.querySelector('variant-picker script[type="application/json"]')?.textContent ||
    documentRoot?.querySelector('variant-picker script[type="application/json"]')?.textContent
  );
}

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const variantIdForForm = selectedOption.dataset.variantId;
    if (isOnProductPage && variantIdForForm) {
      const section = this.closest('.shopify-section');
      const hidden = section?.querySelector('input[ref="variantId"]');
      if (hidden instanceof HTMLInputElement) hidden.value = variantIdForForm;
    }

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    const productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    const url = new URL(productUrl || window.location.href, window.location.origin);

    this.#pendingRequestUrl = url.toString();

    if (this.selectedOptionsValues.length && !source) {
      url.searchParams.set('option_values', this.selectedOptionsValues.join(','));
      const variantId = selectedOption.dataset.variantId;
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        url.searchParams.set('option_values', sourceSelectedOptionsValues.join(','));
      } else if (selectedOption.dataset.optionValueId) {
        url.searchParams.set('option_values', selectedOption.dataset.optionValueId);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      url.search = '';
      url.searchParams.set('section_id', 'section-rendering-product-card');
      if (this.selectedOptionsValues.length && source === 'product-card') {
        url.searchParams.set('option_values', sourceSelectedOptionsValues.join(','));
      } else if (selectedOption.dataset.optionValueId) {
        url.searchParams.set('option_values', selectedOption.dataset.optionValueId);
      }
    }

    return url.toString();
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const expectedVariantId = this.selectedOption?.dataset?.variantId;
        const matchedPicker = findVariantPickerInDocument(html, this);
        const responseRoot = matchedPicker?.closest('.shopify-section') || html;

        const textContent = resolveVariantJsonText(responseRoot, matchedPicker, expectedVariantId, html);
        if (!textContent) return;

        let variantResource;
        try {
          variantResource = JSON.parse(textContent);
        } catch (e) {
          console.error('variant-picker: invalid variant JSON', e);
          return;
        }

        if (
          expectedVariantId &&
          variantResource?.id != null &&
          String(variantResource.id) !== String(expectedVariantId)
        ) {
          variantResource = { ...variantResource, id: expectedVariantId };
        }

        const selectedEl = this.querySelector('select option[selected], fieldset input:checked');
        const sourceOptionId = selectedEl?.dataset?.optionValueId;
        if (!sourceOptionId) {
          console.warn('variant-picker: missing option value id on variant change');
          return;
        }

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);
          if (this.hasAttribute('data-skip-morph')) {
            this.#syncLiveProductState(responseRoot, variantResource);
          }

          this.dispatchEvent(
            new VariantUpdateEvent(variantResource, sourceOptionId, {
              html: responseRoot,
              productId: this.dataset.productId ?? '',
              newProduct,
              variantPickerBlockId: this.dataset.blockId ?? '',
            })
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = findVariantPickerInDocument(newHtml, this);

    if (!newVariantPickerSource) {
      if (this.hasAttribute('data-skip-morph')) {
        return undefined;
      }
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    if (this.hasAttribute('data-skip-morph')) {
      return newProduct;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /** Syncs price, ATC, and hidden variant inputs when picker morph is skipped. */
  #syncLiveProductState(responseRoot, variantResource) {
    const liveRoot = this.closest('.shopify-section, dialog') || this;
    const variantId = variantResource?.id ? String(variantResource.id) : '';

    if (variantId) {
      for (const input of liveRoot.querySelectorAll('[ref="variantId"]')) {
        if (input instanceof HTMLInputElement) input.value = variantId;
      }
      for (const wishlist of liveRoot.querySelectorAll('.wishlist-engine[data-variant_id]')) {
        if (wishlist instanceof HTMLElement) wishlist.setAttribute('data-variant_id', variantId);
      }
    }

    const currentPrice = liveRoot.querySelector('product-price [ref="priceContainer"]');
    const nextPrice = responseRoot.querySelector('product-price [ref="priceContainer"]');
    if (currentPrice && nextPrice) morph(currentPrice, nextPrice);

    const currentAddToCart = liveRoot.querySelector('[ref="addToCartButton"]');
    const nextAddToCart = responseRoot.querySelector('[ref="addToCartButton"]');
    if (currentAddToCart && nextAddToCart) morph(currentAddToCart, nextAddToCart);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
